import {
  useRentalManagerUserPropertyMultiplexWebApiHandler_create,
  useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { MULTIPLEX_MUTATION_INVALIDATES } from '../constants/FlexiblePropertiesConstants';

import type {
  UserPropertyMultiplexCreateUnitRequestWebDto,
  UserPropertyMultiplexCreateUnitResponseWebDto,
  UserPropertyMultiplexCreateUnitsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type CreateMultiplexResult = {
  multiplexBuildingSessionId: string;
  allUnitsFailed: boolean;
  hasPartialUnitsSucceeded: boolean;
  successfulUnits: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
  failedUnits: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
};

export type MultiplexCreateCallbacks = {
  onSuccess: (result: CreateMultiplexResult) => void;
  onError: (errorMessage: string) => void;
};

export const handleMultiplexCreateResponse = (
  response: UserPropertyMultiplexCreateUnitsResponseWebDto | null | undefined,
  callbacks: MultiplexCreateCallbacks,
) => {
  const responseUnits = response?.units ?? [];
  const multiplexBuildingSessionId = response?.multiplexBuildingSessionId ?? '';

  const failedUnits = responseUnits.filter((unit) => !unit.success);
  const successfulUnits = responseUnits.filter((unit) => unit.success);
  const allUnitsFailed = responseUnits.length > 0 && responseUnits.every((unit) => !unit.success);
  const hasPartialUnitsSucceeded = successfulUnits.length > 0 && failedUnits.length > 0;

  if (allUnitsFailed) {
    callbacks.onError(failedUnits.map((unit) => `Unit ${unit.unitNumber}: ${unit.errorMessage}`).join(' '));
    return;
  }

  callbacks.onSuccess({
    multiplexBuildingSessionId,
    allUnitsFailed,
    hasPartialUnitsSucceeded,
    successfulUnits,
    failedUnits,
  });
};

type UseMultiplexUnitCreationParams = {
  fullAddress: string;
  customerId?: string;
};

export const useMultiplexUnitCreation = ({ fullAddress, customerId }: UseMultiplexUnitCreationParams) => {
  const {
    data: unitNumbersResponse,
    error: unitNumbersError,
    isError: isErrorUnitNumbers,
    isLoading: isLoadingUnitNumbers,
  } = useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers(
    { fullAddress },
    {
      meta: {
        tags: ['getUnitNumbers'],
        context: { api: 'rental-manager', details: 'useMultiplexUnitCreation' },
      },
    },
  );

  const existingUnitNumbers = unitNumbersResponse?.unitNumbers ?? [];

  const { isPending, mutate: createMultiplexMutation } = useRentalManagerUserPropertyMultiplexWebApiHandler_create({
    meta: {
      invalidates: [...MULTIPLEX_MUTATION_INVALIDATES],
      context: { api: 'rental-manager', details: 'useMultiplexUnitCreation' },
    },
  });

  const createMultiplex = (
    units: Array<UserPropertyMultiplexCreateUnitRequestWebDto>,
    callbacks: MultiplexCreateCallbacks,
  ) => {
    createMultiplexMutation(
      {
        postData: {
          ...(customerId && { customerId }),
          fullAddress,
          units,
        },
      },
      {
        onSuccess: (response) => handleMultiplexCreateResponse(response, callbacks),
        onError: (error) => {
          callbacks.onError(DatarouterError.getDisplayMessage(error));
        },
      },
    );
  };

  return {
    existingUnitNumbers,
    isLoadingUnitNumbers,
    isErrorUnitNumbers,
    unitNumbersError,
    isPending,
    createMultiplex,
  };
};
